import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

/**
 * Reusable Countdown Timer Component
 * Used for:
 * 1. Custom Offer/Request expiration (24 hours)
 * 2. Order delivery deadlines
 * 3. Hourly gig overtime tracking
 */
const CountdownTimer = ({
    targetDate,
    onExpire,
    type = 'expiration',  // 'expiration', 'delivery', or 'overtime'
    showIcon = true,
    className = ''
}) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
    const [expired, setExpired] = useState(false);

    function calculateTimeLeft() {
        const difference = new Date(targetDate) - new Date();

        if (difference <= 0) {
            return { expired: true };
        }

        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        return {
            days,
            hours,
            minutes,
            seconds,
            totalMinutes: Math.floor(difference / (1000 * 60)),
            expired: false
        };
    }

    useEffect(() => {
        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft();
            setTimeLeft(newTimeLeft);

            // Call onExpire callback when time runs out
            if (newTimeLeft.expired && !expired) {
                setExpired(true);
                if (onExpire) {
                    onExpire();
                }
            }
        }, 1000); // Update every second

        return () => clearInterval(timer);
    }, [targetDate, expired, onExpire]);

    const getDisplayText = () => {
        if (timeLeft.expired) {
            if (type === 'expiration') return 'Expired';
            if (type === 'delivery') return 'Overdue';
            if (type === 'overtime') return 'In Overtime';
        }

        const { days, hours, minutes, seconds, totalMinutes } = timeLeft;

        // For offers/requests (usually <24h)
        if (type === 'expiration') {
            if (hours >= 1) {
                return `${hours}h ${minutes}m`;
            } else if (minutes >= 1) {
                return `${minutes}m ${seconds}s`;
            } else {
                return `${seconds}s`;
            }
        }

        // For order delivery (could be days)
        if (type === 'delivery' || type === 'overtime') {
            if (days > 0) {
                return `${days}d ${hours}h`;
            } else if (hours > 0) {
                return `${hours}h ${minutes}m`;
            } else if (minutes > 0) {
                return `${minutes}m`;
            } else {
                return `${seconds}s`;
            }
        }

        return '';
    };

    const getColorClass = () => {
        if (timeLeft.expired) {
            return 'text-red-600 font-semibold';
        }

        const { totalMinutes } = timeLeft;

        // Critical (<10 minutes or <1 hour depending on type)
        if (type === 'expiration' && totalMinutes < 10) {
            return 'text-red-600 font-semibold';
        }
        if (type === 'delivery' && totalMinutes < 60) {
            return 'text-red-600 font-semibold';
        }

        // Warning
        if (type === 'expiration' && totalMinutes < 60) {
            return 'text-orange-500 font-medium';
        }
        if (type === 'delivery' && totalMinutes < 360) { // <6 hours
            return 'text-orange-500 font-medium';
        }

        // Normal
        return 'text-gray-600';
    };

    const getIconColor = () => {
        if (timeLeft.expired) return 'text-red-600';
        if (timeLeft.totalMinutes < 10) return 'text-red-600';
        if (timeLeft.totalMinutes < 60) return 'text-orange-500';
        return 'text-blue-600';
    };

    const shouldPulse = timeLeft.expired || (timeLeft.totalMinutes && timeLeft.totalMinutes < 10);

    return (
        <div className={`flex items-center space-x-1 ${className}`}>
            {showIcon && (
                <div className={shouldPulse ? 'animate-pulse' : ''}>
                    {timeLeft.expired ? (
                        <AlertTriangle className={`w-4 h-4 ${getIconColor()}`} />
                    ) : (
                        <Clock className={`w-4 h-4 ${getIconColor()}`} />
                    )}
                </div>
            )}
            <span className={`text-sm ${getColorClass()}`}>
                {type === 'expiration' && !timeLeft.expired && 'Expires in: '}
                {type === 'delivery' && !timeLeft.expired && 'Due in: '}
                {type === 'overtime' && !timeLeft.expired && 'Time left: '}
                {getDisplayText()}
            </span>
        </div>
    );
};

export default CountdownTimer;
